
import { useState } from 'react';
import { Link } from 'react-router-dom';

// Turnstile CAPTCHA type declaration
declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: {
        sitekey: string;
        theme?: 'light' | 'dark' | 'auto';
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
    };
  }
}

// Turnstile Site Key
const TURNSTILE_SITE_KEY = '0x4AAAAAACHI6UJ5Gjz87OBO';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setSubmitMessage('Please enter your email address');
      return;
    }

    // Get Turnstile token
    const turnstileToken = window.turnstile?.getResponse?.();
    if (!turnstileToken) {
      setSubmitMessage('Please complete the CAPTCHA verification');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('/api/newsletter.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, turnstileToken })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitMessage(result.message || 'Successfully subscribed to newsletter!');
        setEmail('');
        window.turnstile?.reset?.();
      } else {
        setSubmitMessage(result.error || 'Subscription failed. Please try again.');
        window.turnstile?.reset?.();
      }
    } catch (error) {
      setSubmitMessage('Network error. Please try again.');
      window.turnstile?.reset?.();
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <footer className="bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Single Glass-Effect Card Container - All Content Inside */}
        <div className="relative overflow-hidden bg-white/60 backdrop-blur-2xl border border-white/30 rounded-3xl shadow-2xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
          
          {/* Decorative elements - clipped inside card */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
            <div className="absolute top-8 right-8 w-16 h-16 bg-blue-200/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute bottom-8 left-8 w-12 h-12 bg-teal-200/25 rounded-full blur-lg animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/4 w-8 h-8 bg-purple-200/20 rounded-full blur-md animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          {/* Content wrapper with proper z-index */}
          <div className="relative" style={{ zIndex: 1 }}>
            {/* Logo Section */}
            <div className="mb-12">
              <Link to="/" className="flex items-center space-x-3 group">
                <img
                  src="/images/logo/logo-120x120.png"
                  alt="GalaxyFabTech Logo"
                  className="w-12 h-12 object-contain transition-transform duration-300 group-hover:scale-105"
                />
                <div>
                  <span className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <span className="text-[#1e3a5f]">GALAXY</span>
                    <span className="text-[#0d9488]">FABTECH</span>
                  </span>
                  <div className="text-xs text-teal-600 font-medium">Pvt Ltd</div>
                </div>
              </Link>
            </div>

            {/* Four Column Grid - Responsive */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 xl:gap-12">
              
              {/* Column 1 - Company Description */}
              <div className="space-y-6">
                <p className="text-gray-600 text-sm leading-relaxed">
                  Complete Laundry Solutions with Unmatched Service Support. Empowering businesses with quality equipment and excellence across India.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-gray-400 hover:text-blue-600 transition-all duration-500 hover:scale-103 relative group">
                    <i className="ri-facebook-line text-lg"></i>
                    <div className="absolute inset-0 rounded-full border border-blue-400/50 scale-125 opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500"></div>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-blue-600 transition-all duration-500 hover:scale-103 relative group">
                    <i className="ri-twitter-line text-lg"></i>
                    <div className="absolute inset-0 rounded-full border border-blue-400/50 scale-125 opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500"></div>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-blue-600 transition-all duration-500 hover:scale-103 relative group">
                    <i className="ri-linkedin-line text-lg"></i>
                    <div className="absolute inset-0 rounded-full border border-blue-400/50 scale-125 opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500"></div>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-blue-600 transition-all duration-500 hover:scale-103 relative group">
                    <i className="ri-instagram-line text-lg"></i>
                    <div className="absolute inset-0 rounded-full border border-blue-400/50 scale-125 opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500"></div>
                  </a>
                </div>
              </div>

              {/* Column 2 - Quick Links */}
              <div>
                <h3 className="text-sm font-extrabold text-gray-800 uppercase tracking-wider mb-8">Quick Links</h3>
                <ul className="space-y-4">
                  <li>
                    <Link to="/about" className="footer-link text-gray-600 transition-all duration-500 text-sm relative inline-block">
                      About Us
                    </Link>
                  </li>
                  <li>
                    <Link to="/products" className="footer-link text-gray-600 transition-all duration-500 text-sm relative inline-block">
                      Products
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy-policy" className="footer-link text-gray-600 transition-all duration-500 text-sm relative inline-block">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms-and-conditions" className="footer-link text-gray-600 transition-all duration-500 text-sm relative inline-block">
                      Terms and Conditions
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Column 3 - Our Services */}
              <div>
                <h3 className="text-sm font-extrabold text-gray-800 uppercase tracking-wider mb-8">Our Services</h3>
                <ul className="space-y-4">
                  <li className="text-gray-600 text-sm">Equipment Consultation</li>
                  <li className="text-gray-600 text-sm">Project Planning</li>
                  <li className="text-gray-600 text-sm">Installation & Commissioning</li>
                  <li className="text-gray-600 text-sm">After-sales Support</li>
                  <li className="text-gray-600 text-sm">Technical Training</li>
                </ul>
              </div>

              {/* Column 4 - Newsletter & Contact */}
              <div>
                <h3 className="text-sm font-extrabold text-gray-800 uppercase tracking-wider mb-8">Stay Updated</h3>
                
                {/* Newsletter Form - Fully contained within card */}
                <form
                  onSubmit={handleNewsletterSubmit}
                  className="mb-8"
                >
                  <div className="flex rounded-xl overflow-hidden shadow-sm mb-3">
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 min-w-0 px-4 py-3 text-sm border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-gray-50 to-white transition-all duration-500"
                      disabled={isSubmitting}
                      style={{ borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px', borderRight: 'none' }}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="footer-button flex-shrink-0 px-4 lg:px-6 py-3 bg-gray-800 text-white text-sm font-medium transition-all duration-500 focus:outline-none whitespace-nowrap disabled:opacity-50 relative min-w-[100px]"
                      style={{ borderTopRightRadius: '12px', borderBottomRightRadius: '12px' }}
                    >
                      {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </div>
                  {/* Turnstile CAPTCHA */}
                  <div className="cf-turnstile mb-3" data-sitekey={TURNSTILE_SITE_KEY} data-theme="light" data-size="compact"></div>
                  {submitMessage && (
                    <p className={`text-xs mt-3 ${
                      submitMessage.includes('Successfully') || submitMessage.includes('success') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {submitMessage}
                    </p>
                  )}
                </form>

                {/* Contact Details */}
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-sm font-bold text-gray-800">+91 99808 72453</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Email</p>
                    <p className="text-sm font-bold text-gray-800">info@galaxyfabtech.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Thin Divider Line Below Card */}
        <div className="relative mt-12 mb-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-6 text-gray-400">
              <i className="ri-star-line text-sm"></i>
            </span>
          </div>
        </div>

        {/* Footer Bottom Strip - Outside Glass Card, Plain Styling */}
        <div className="pt-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            {/* Copyright - Center */}
            <div className="text-center">
              <p className="text-base text-gray-500">
                © 2025 GalaxyFabTech. All rights reserved. 
                <span className="mx-2">•</span>
                <a 
                  href="https://www.luminious.net" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-gray-700 transition-colors"
                >
                  Developed by Luminious
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Custom CSS for enhanced hover effects */}
        <style jsx>{`
          :root {
            --color-accent: #2563eb;
          }
          
          .footer-link {
            position: relative;
            display: inline-block;
            transition: color 0.3s ease;
          }
          
          .footer-link::after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: -4px;
            left: 0;
            background: linear-gradient(90deg, #2563eb, #06b6d4);
            transition: width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            border-radius: 1px;
          }
          
          .footer-link:hover {
            color: #2563eb !important;
          }
          
          .footer-link:hover::after {
            width: 100%;
          }
          
          .footer-button {
            position: relative;
            overflow: hidden;
          }
          
          .footer-button:hover {
            background-color: #374151 !important;
            color: var(--color-accent) !important;
            transform: translateY(-2px) scale(1.02);
            filter: drop-shadow(0 4px 12px rgba(37, 99, 235, 0.2));
          }
          
          .footer-button:focus {
            outline: 2px solid var(--color-accent);
            outline-offset: 2px;
          }
          
          .hover\\:scale-103:hover {
            transform: scale(1.03);
          }
          
          .hover\\:scale-101:hover {
            transform: scale(1.01);
          }
          
          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }
        `}</style>
      </div>
    </footer>
  );
}
